import { EnvironmentDetail } from '@amzn/ihm-associate-tools-js-lib'
import { authService } from '@services/auth/AuthService'
import { getActiveAirportCode, getAirportCodeForStore } from '@utils/RegionRealms'
import { parseAppHost } from '@utils/AppHostUtil'

export const _getParameterByName = (param: string, url?: string) => {
    if (!url) {
        url = window.location.toString()
    }

    param = param.replace(/[\[\]]/g, '\\$&')
    const regex = new RegExp('[?&]' + param + '(=([^&#]*)|&|#|$)', 'i')
    const results = regex.exec(url)
    if (!results) return null
    if (!results[2]) return ''
    return decodeURIComponent(results[2].replace(/\+/g, ' '))
}

export const isAndroid = EnvironmentDetail.isAndroid

const SCAN_INTO_STORE_CLIENT_IDS = new Set([
    '1u79uaco64ls3kei9e7esk3tkh', // jwo-gamma.com-client
    '3puobfke7u9knq5l6rs03644j4', // jwo-prod.com-client
    '22o1pdm2moru8rk9sn89dqu41e', // jwo-prod-pdx.com-client (us-west-2_y8irWq9O1)
])

export const isScanIntoStoreEnabled = (): boolean => {
    const clientId = authService.getClientId() || localStorage.getItem('clientId')?.replace(/"/g, '')
    return SCAN_INTO_STORE_CLIENT_IDS.has(clientId ?? '') || getDomain() !== 'prod' || _getParameterByName('scanIntoStore') === 'true'
}

export const getDomain = EnvironmentDetail.getDomain

const PLATFORM_HOSTNAME_PATTERN = /^[^.]+\.([^.]+)\.storemanagement\.jihm\.amazon\.dev$/

/**
 * The deployment domain if one can be determined: hostname first (a storemanagement.jihm.amazon.dev
 * host or a regional `{awsRegion}.{stage}.app.justwalkout.com` host), then the `domain` param.
 */
const resolveDomain = (): string | undefined => {
    const hostname = window.location.hostname
    const appHost = parseAppHost(hostname)
    if (appHost) return appHost.stage
    const domainMatch = hostname.match(PLATFORM_HOSTNAME_PATTERN)
    return domainMatch ? domainMatch[1] : (getDomain() || undefined)
}

/**
 * Resolves the current deployment domain from the hostname, falling back to EnvironmentDetail.
 * e.g. us-west-2.gamma.storemanagement.jihm.amazon.dev -> 'gamma',
 *      eu-west-1.prod.app.justwalkout.com -> 'prod'
 */
export const getDomainFromHostname = (): string => resolveDomain() || 'test'

/**
 * Platform domains, not asset domains -- the platform's 'test' is registered as 'beta'.
 */
const BOTTOM_NAV_DOMAINS = new Set(['alpha', 'test', 'gamma', 'prod'])

/** Unlike getDomainFromHostname this has no 'test' default: an unknown session keeps the side nav. */
export const isBottomNavEnabled = (): boolean => BOTTOM_NAV_DOMAINS.has(resolveDomain() ?? '')

/**
 * The cell whose resources this session should use, as an airport code.
 *
 * Once a store is selected that store's own cell wins: a tenant's Cognito pool can be in IAD
 * while its stores, Virgo fleet and tool backends are in PDX, and reading those off the login
 * cell is what produced Planogram "Failed to connect to <store>.us-east-1.ihmvirgo..." and User
 * Management "User pool ... does not exist". Before a store is selected there is nothing better
 * to go on than the cell we were pinned to.
 *
 * This replaces a per-tenant PDX client-ID denylist, which had to be extended by hand for every
 * new PDX tenant, pinned the whole session rather than just the store-scoped calls, and returned
 * an AWS region id where every other caller of this function returns an airport code — silently
 * defeating the `region === VALID_REGIONS.IAD` comparison in storeSlice.setCurrentDomainAndRegion.
 */
export const getRegion = (): string => getActiveAirportCode()

/**
 * The cell owning a given store, or undefined while it is unknown.
 *
 * Needed wherever a store id travels separately from the session's own notion of "current store":
 * a tool URL carries one specific store, and the two can disagree (Associate Entry is handed the
 * store just picked while the scanned store is still the current one).
 */
export const getRegionForStore = (storeId: string | undefined): string | undefined =>
    getAirportCodeForStore(storeId)

export const getStoreId = EnvironmentDetail.getStoreId

export const getStoreFamily = EnvironmentDetail.getStoreFamily

export const getDevicePlatform = EnvironmentDetail.getDevicePlatform
